import { Icon } from '@aqarmap/aqarmap-ui';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '~/hooks';

type PaginationProps = {
  currentPage: number;
  totalCount: number;
  limit?: number;
  onPageChange: (page: number) => void;
  className?: string;
  showEdgeNavigation?: boolean;
  maxVisiblePages?: number;
};

export default function Pagination({
  currentPage,
  totalCount,
  limit = 10,
  onPageChange,
  className = '',
  showEdgeNavigation = true,
}: PaginationProps) {
  const { i18n } = useTranslation('app');
  const locale = i18n.language;
  const { mobileScreen } = useBreakpoint();
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const maxVisiblePages = mobileScreen ? 3 : 9;
  const halfWindow = Math.floor(maxVisiblePages / 2);

  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i,
  );

  if (totalPages <= 1) return null;

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const buttonBase =
    'flex h-[40px] w-[40px] items-center justify-center rounded-[10px] text-link ';

  const buttonClassName = `${buttonBase}  border border-gray text-gray__dark_1 hover:bg-gray_light_1 hover:text-blue`;

  return (
    <div
      className={`flex items-center justify-center gap-1.5x ${className} pagination`}
    >
      {showEdgeNavigation && canGoPrev && (
        <button
          type="button"
          className={buttonClassName}
          aria-label="First page"
          onClick={() => onPageChange(1)}
        >
          <Icon
            name="chevron-double-right"
            size="15px"
            className={locale === 'en' ? 'rotate-180' : ''}
          />
        </button>
      )}

      {canGoPrev && (
        <button
          type="button"
          className={buttonClassName}
          aria-label="Previous page"
          onClick={() => onPageChange(currentPage - 1)}
        >
          <Icon
            name="chevron-right"
            size="15px"
            className={locale === 'en' ? 'rotate-180' : ''}
          />
        </button>
      )}

      {pages.map((page) => {
        const isActive = page === currentPage;
        return (
          <button
            key={page}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            className={[
              buttonBase,
              isActive
                ? 'bg-blue text-white hover:bg-blue_dark_1 hover:text-white border-0'
                : ' text-gray__dark_1 hover:bg-gray_light_1 hover:text-blue bg-white border border-gray  ',
            ].join(' ')}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        );
      })}

      <button
        type="button"
        className={buttonClassName}
        aria-label="Next page"
        disabled={!canGoNext}
        onClick={() => canGoNext && onPageChange(currentPage + 1)}
      >
        <Icon
          name="chevron-left"
          size="15px"
          className={locale === 'en' ? 'rotate-180' : ''}
        />
      </button>

      {showEdgeNavigation && (
        <button
          type="button"
          className={buttonClassName}
          aria-label="Last page"
          disabled={!canGoNext}
          onClick={() => canGoNext && onPageChange(totalPages)}
        >
          <Icon
            name="chevron-double-right"
            size="15px"
            className={locale === 'ar' ? 'rotate-180' : ''}
          />
        </button>
      )}
    </div>
  );
}
